import React from 'react'
import { ChartLine, Clock, Headset, MessageSquare, RefreshCw, ShieldCheck, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import PlanCard, { type PlanCardCta } from './PlanCard'
import { resolveText, splitBold } from './render'
import type { CardContent, CardPlan, PaywallContent, ReassuranceItem } from './types'

/**
 * The paywall page around the plan cards: title, intro, cards, reassurance row and
 * footnote. Each text the organisation has not set is the built-in one. Status
 * messages (ended subscription, payment returns, errors) are the caller's, through
 * `notice` and `error`, and are never replaced by organisation content.
 */

export const REASSURANCE_ICONS: Record<ReassuranceItem['icon'], React.ComponentType<{ className?: string }>> = {
  refresh: RefreshCw,
  shield: ShieldCheck,
  clock: Clock,
  headset: Headset,
  chart: ChartLine,
  message: MessageSquare,
}

export interface PaywallPlan extends CardPlan {
  id: number
  card: CardContent
}

interface PaywallPageProps {
  plans: PaywallPlan[]
  content: PaywallContent | null | undefined
  language: string
  /** Built-in line under the intro, such as "your subscription has ended". */
  notice?: React.ReactNode
  /** Shown in place of the cards (loading, nothing to offer). */
  empty?: React.ReactNode
  error?: React.ReactNode
  ctaFor?: (plan: PaywallPlan) => PlanCardCta
}

const Plain: React.FC<{ text: string }> = ({ text }) => (
  <>{splitBold(text).map((part, i) => (part.bold ? <strong key={i}>{part.text}</strong> : <React.Fragment key={i}>{part.text}</React.Fragment>))}</>
)

const PaywallPage: React.FC<PaywallPageProps> = ({ plans, content, language, notice, empty, error, ctaFor }) => {
  const { i18n } = useTranslation()
  const t = i18n.getFixedT(language)
  const title = resolveText(content?.title, language) || t('subscription.paywall.title')
  const intro = resolveText(content?.intro, language) || t('subscription.paywall.body')
  // The built-in footnote gains a sentence when a one-time plan is on offer; a partner's own text is left as written.
  const footnote = resolveText(content?.footnote, language) || [t('subscription.paywall.noRollover'), plans.some((p) => !p.renews) ? t('subscription.paywall.oneTime') : ''].filter(Boolean).join(' ')
  const reassurance = content?.reassurance ?? []

  return (
    <div className="w-full max-w-6xl font-[Inter]">
      <div className="mb-9 text-center">
        <div className="mx-auto mb-4 flex h-[54px] w-[54px] items-center justify-center rounded-[14px] border border-[#D6EBDE] bg-[#E8F5EE] text-[#0F4C3A]">
          <Sparkles className="h-6 w-6" />
        </div>
        <h1 className="mb-3 text-3xl font-extrabold leading-tight tracking-[-1px] text-[#0F4C3A] md:text-4xl">
          <Plain text={title} />
        </h1>
        <p className="mx-auto max-w-xl text-[15.5px] leading-relaxed text-[#6B7D72]">
          <Plain text={intro} />
        </p>
        {notice}
      </div>

      {plans.length === 0 ? (
        empty
      ) : (
        <div className="mb-12 flex flex-wrap items-stretch justify-center gap-5">
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} card={plan.card} language={language} cta={ctaFor?.(plan)} className="w-full md:w-[calc((100%-2.5rem)/3)] md:max-w-[22rem]" />
          ))}
        </div>
      )}

      {error && <div className="-mt-6 mb-6 text-center text-sm text-[#B42318]">{error}</div>}

      {plans.length > 0 && reassurance.length > 0 && (
        <div
          className={cn(
            'mx-auto mb-6 grid gap-6 rounded-2xl border border-[#E8EDE9] bg-white px-6 py-7 md:px-10',
            reassurance.length === 1 && 'max-w-md',
            reassurance.length === 2 && 'max-w-3xl md:grid-cols-2',
            reassurance.length === 3 && 'md:grid-cols-3',
            reassurance.length === 4 && 'md:grid-cols-2 lg:grid-cols-4',
          )}
        >
          {reassurance.map((item, i) => {
            const Icon = REASSURANCE_ICONS[item.icon] ?? ShieldCheck
            return (
              <div key={i} className="flex items-start gap-3.5">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#E8F5EE] text-[#0F4C3A]">
                  <Icon className="h-[18px] w-[18px]" />
                </div>
                <div>
                  <div className="mb-0.5 text-[13px] font-bold text-[#0F4C3A]">{resolveText(item.title, language)}</div>
                  <div className="text-[12.5px] leading-snug text-[#6B7D72]">
                    <Plain text={resolveText(item.text, language)} />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {plans.length > 0 && (
        <p className="mx-auto max-w-2xl text-center text-[12.5px] leading-relaxed text-[#6B7D72]">
          <Plain text={footnote} />
        </p>
      )}
    </div>
  )
}

export default PaywallPage
